import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import MobileInput, { isValidMobile } from "@/components/MobileInput";

export default function CrudDialog({ open, setOpen, title, fields, value, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => (base[f.name] = f.type === "boolean" ? true : ""));
      setForm({ ...base, ...(value || {}) });
    }
  }, [open, value]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Validate mobile fields
    for (const f of fields) {
      if (f.type === "mobile") {
        const v = form[f.name];
        if (f.required && !isValidMobile(v)) {
          e.stopPropagation();
          alert(`${f.label} must be exactly 10 digits`);
          return;
        }
        if (!f.required && v && !isValidMobile(v)) {
          alert(`${f.label} must be exactly 10 digits`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      await onSave(form);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl bg-white" data-testid="crud-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label className="text-xs font-semibold uppercase tracking-wider">{f.label}</Label>
              {f.type === "select" ? (
                <Select value={String(form[f.name] ?? "")} onValueChange={(v) => set(f.name)(v)}>
                  <SelectTrigger className="h-10" data-testid={`field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {(f.options || []).map((o) => (
                      <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "boolean" ? (
                <Select value={form[f.name] ? "true" : "false"} onValueChange={(v) => set(f.name)(v === "true")}>
                  <SelectTrigger className="h-10" data-testid={`field-${f.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea rows={3} value={form[f.name] ?? ""} onChange={(e) => set(f.name)(e.target.value)} data-testid={`field-${f.name}`} />
              ) : f.type === "mobile" ? (
                <MobileInput
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name)(e.target.value)}
                  required={f.required}
                  className="h-10"
                  data-testid={`field-${f.name}`}
                />
              ) : (
                <Input
                  type={f.type || "text"}
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name)(f.type === "number" ? Number(e.target.value) : e.target.value)}
                  required={f.required}
                  className="h-10"
                  data-testid={`field-${f.name}`}
                />
              )}
            </div>
          ))}
          <DialogFooter className="sm:col-span-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white" data-testid="dialog-save-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
